import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Heart, Users, UserCheck, Star, ArrowRight, CheckCircle, MessageCircle, Crown, Phone } from 'lucide-react';
import api from '../lib/api';
import Footer from '../components/Footer';

function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (target <= 0 || started.current) return;
    const startCount = () => {
      if (started.current) return;
      started.current = true;
      let start = 0;
      const step = target / (duration / 16);
      const timer = setInterval(() => {
        start += step;
        if (start >= target) { setCount(target); clearInterval(timer); }
        else setCount(Math.floor(start));
      }, 16);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { startCount(); observer.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    // Fallback: start after 3s if not yet triggered
    const fallback = setTimeout(() => { if (!started.current) startCount(); }, 3000);
    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, [target, duration]);
  return [count, ref];
}

const HERO_IMG = "https://images.unsplash.com/photo-1636381427368-3abc235833b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxtYXJpZ29sZCUyMGZsb3dlcnMlMjBpbmRpYW4lMjBkZWNvcmF0aW9ufGVufDB8fHx8MTc3MDY0NTU4MXww&ixlib=rb-4.1.0&q=85";

const successStories = [
  { name_en: "Priya & Rahul", name_hi: "प्रिया और राहुल", name_mr: "प्रिया आणि राहुल", city: "Mumbai", quote_en: "We found each other on VivahRang and knew it was meant to be.", quote_hi: "हमने विवाहरंग पर एक दूसरे को पाया और जाना कि यह होना ही था।", quote_mr: "आम्ही विवाहरंग वर एकमेकांना शोधले." },
  { name_en: "Sneha & Amit", name_hi: "स्नेहा और अमित", name_mr: "स्नेहा आणि अमित", city: "Pune", quote_en: "The verified profiles gave us confidence. Thank you VivahRang!", quote_hi: "सत्यापित प्रोफाइल ने हमें भरोसा दिया। धन्यवाद विवाहरंग!", quote_mr: "सत्यापित प्रोफाइलने आम्हाला आत्मविश्वास दिला." },
  { name_en: "Meera & Suresh", name_hi: "मीरा और सुरेश", name_mr: "मीरा आणि सुरेश", city: "Nagpur", quote_en: "Our families connected first and we bonded over shared values.", quote_hi: "पहले हमारे परिवार जुड़े और फिर हम साझा मूल्यों पर जुड़ गए।", quote_mr: "आधी आमचे कुटुंब जोडले गेले आणि मग आम्ही सामायिक मूल्यांवर जोडले गेलो." },
];

export default function LandingPage() {
  const { t, lang, formatCurrency } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_users: 15000, verified_users: 8000, total_matches: 3500 });
  const [plans, setPlans] = useState([]);
  const [usersCount, usersRef] = useCountUp(stats.total_users);
  const [verifiedCount, verifiedRef] = useCountUp(stats.verified_users);
  const [matchesCount, matchesRef] = useCountUp(stats.total_matches);

  useEffect(() => {
    api.get('/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/plans').then(r => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const steps = [
    { icon: UserCheck, color: 'bg-[#D9381E]', title: t('how_it_works.step1_title'), desc: t('how_it_works.step1_desc') },
    { icon: Heart, color: 'bg-[#F2A900]', title: t('how_it_works.step2_title'), desc: t('how_it_works.step2_desc') },
    { icon: ShieldCheck, color: 'bg-green-600', title: t('how_it_works.step3_title'), desc: t('how_it_works.step3_desc') },
    { icon: MessageCircle, color: 'bg-[#D9381E]', title: t('how_it_works.step4_title'), desc: t('how_it_works.step4_desc') },
  ];

  return (
    <div className="overflow-hidden" data-testid="landing-page">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} className="w-full h-full object-cover" alt="Marigold decoration" loading="eager" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl stagger-children">
            <Badge className="bg-[#F2A900]/20 text-[#F2A900] border-[#F2A900]/30 mb-6 text-sm px-4 py-1.5">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> {t('trust.aadhaar')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              {t('hero.headline')}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
              {t('hero.subtitle')}
            </p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <Button
                onClick={() => navigate(user ? '/search' : '/auth')}
                className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
                data-testid="hero-register-btn"
              >
                {t('hero.cta_register')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(user ? '/search' : '/auth')}
                className="border-2 border-white/80 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold bg-transparent"
                data-testid="hero-search-btn"
              >
                {t('hero.cta_search')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-6 bg-white border-b border-[#E7E5E4]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {[
              { icon: ShieldCheck, text: t('trust.aadhaar'), color: 'text-green-600' },
              { icon: Users, text: t('trust.verified'), color: 'text-[#D9381E]' },
              { icon: CheckCircle, text: t('trust.secure'), color: 'text-[#F2A900]' },
              { icon: Star, text: t('trust.privacy'), color: 'text-[#BFA588]' },
            ].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-medium text-[#2D2424]">
                <badge.icon className={`w-5 h-5 ${badge.color}`} />
                {badge.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section className="py-16 bg-[#FFF8F0]" data-testid="stats-section">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          <div ref={usersRef}>
            <div className="text-4xl sm:text-5xl font-bold text-[#D9381E]">{usersCount.toLocaleString('en-IN')}+</div>
            <div className="mt-2 text-sm text-[#78716C] font-medium">{t('stats.users')}</div>
          </div>
          <div ref={verifiedRef}>
            <div className="text-4xl sm:text-5xl font-bold text-[#F2A900]">{verifiedCount.toLocaleString('en-IN')}+</div>
            <div className="mt-2 text-sm text-[#78716C] font-medium">{t('stats.verified')}</div>
          </div>
          <div ref={matchesRef}>
            <div className="text-4xl sm:text-5xl font-bold text-green-600">{matchesCount.toLocaleString('en-IN')}+</div>
            <div className="mt-2 text-sm text-[#78716C] font-medium">{t('stats.matches')}</div>
          </div>
        </div>
      </section>

      <div className="marigold-divider max-w-xs mx-auto" />

      {/* How It Works */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2D2424]">{t('how_it_works.title')}</h2>
            <p className="mt-3 text-base text-[#78716C]">{t('how_it_works.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children">
            {steps.map((step, i) => (
              <Card key={i} className="p-6 text-center border border-[#E7E5E4] hover:border-[#F2A900] transition-colors rounded-2xl group">
                <div className={`w-14 h-14 ${step.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-xs font-bold text-[#F2A900] mb-2">0{i + 1}</div>
                <h3 className="text-lg font-bold text-[#2D2424] mb-2">{step.title}</h3>
                <p className="text-sm text-[#78716C]">{step.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-[#FFF8F0]" data-testid="success-stories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2D2424]">{t('success_stories.title')}</h2>
            <p className="mt-3 text-base text-[#78716C]">{t('success_stories.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories.map((story, i) => (
              <Card key={i} className="p-6 bg-white rounded-2xl border border-[#E7E5E4] hover:border-[#F2A900] transition-all hover:-translate-y-1 hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D9381E] to-[#F2A900] flex items-center justify-center text-white font-bold text-lg">
                    {story.name_en[0]}
                  </div>
                  <div>
                    <div className="font-bold text-[#2D2424]">{story[`name_${lang}`] || story.name_en}</div>
                    <div className="text-xs text-[#78716C]">{story.city}</div>
                  </div>
                </div>
                <p className="text-sm text-[#78716C] italic leading-relaxed">"{story[`quote_${lang}`] || story.quote_en}"</p>
                <div className="mt-4 flex">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-[#F2A900] fill-[#F2A900]" />)}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      {plans.length > 0 && (
        <section className="py-20 bg-white" data-testid="pricing-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2D2424]">{t('plans.title')}</h2>
              <p className="mt-3 text-base text-[#78716C]">{t('plans.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan) => (
                <Card
                  key={plan.id}
                  data-popular={plan.is_popular}
                  className={`relative p-6 rounded-2xl border-2 transition-all hover:-translate-y-1 hover:shadow-xl ${
                    plan.is_popular ? 'border-[#F2A900] shadow-lg plan-card-popular' : 'border-[#E7E5E4]'
                  }`}
                >
                  {plan.is_popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F2A900] text-[#2D2424] px-4 py-1 text-xs font-bold">
                      <Crown className="w-3 h-3 mr-1" /> {t('plans.popular')}
                    </Badge>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-[#2D2424]">{plan[`name_${lang}`] || plan.name}</h3>
                    <div className="mt-2">
                      {plan.price === 0 ? (
                        <span className="text-2xl font-bold text-green-600">{t('plans.free_tag')}</span>
                      ) : (
                        <span className="text-3xl font-bold text-[#2D2424]">{formatCurrency(plan.price)}</span>
                      )}
                      {plan.duration_months > 0 && (
                        <span className="text-sm text-[#78716C] ml-1">/ {plan.duration_months} {lang === 'en' ? 'months' : lang === 'hi' ? 'महीने' : 'महिने'}</span>
                      )}
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {Object.entries(plan.features || {}).map(([key, val]) => (
                      <li key={key} className="flex items-center gap-2 text-sm">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 ${val === false ? 'text-[#E7E5E4]' : 'text-green-600'}`} />
                        <span className={val === false ? 'text-[#78716C] line-through' : 'text-[#2D2424]'}>
                          {val === -1 ? t('plans.unlimited') : val === true ? '' : val} {t(`plans.features.${key}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate(user ? '/plans' : '/auth')}
                    className={`w-full rounded-full py-5 font-semibold transition-all ${
                      plan.is_popular ? 'bg-[#D9381E] hover:bg-[#B91C1C] text-white shadow-md' : 'bg-[#2D2424] hover:bg-[#2D2424]/90 text-white'
                    }`}
                    data-testid={`plan-${plan.id}-btn`}
                  >
                    {t('plans.choose')}
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#D9381E] to-[#B91C1C]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('hero.headline')}</h2>
          <p className="mt-4 text-lg text-white/85">{t('hero.subtitle')}</p>
          <Button
            onClick={() => navigate(user ? '/search' : '/auth')}
            className="mt-8 bg-[#F2A900] hover:bg-[#D49000] text-[#2D2424] rounded-full px-10 py-6 text-lg font-bold shadow-xl"
            data-testid="cta-register-btn"
          >
            {t('hero.cta_register')} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
